import { type ScanDataConfigurationInput, type ScanDataFundingSource } from '@occc/graphql';
import type { ScanDataConfigurationFormValues } from '@occc/validation';

import { DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeOfferUpdateOperation } from '@occc-app/offerHelpers/operations/makeOfferUpdateOperation';

export type GetScanDataConfigurationOperationParams = {
  offerId: string;
  dirtyFields: DirtyFieldsType;
  values: { scanDataConfiguration?: ScanDataConfigurationFormValues; [key: string]: any };
};

const buildScanDataConfigurationInput = (
  scanData: ScanDataConfigurationFormValues | undefined,
): ScanDataConfigurationInput => {
  const discountAllocation = scanData?.discountAllocation;
  if (discountAllocation === undefined) {
    throw new Error(
      'Discount allocation is required when Scan Data Configuration is enabled; validate the form before saving.',
    );
  }
  const fundingSourceRaw = scanData?.fundingSource;
  if (!fundingSourceRaw) {
    throw new Error(
      'Funding source is required when Scan Data Configuration is enabled; validate the form before saving.',
    );
  }
  const fundingSource: ScanDataFundingSource = fundingSourceRaw;
  return {
    description: scanData?.description ?? '',
    // Form stores dollars; API expects pennies (Int!)
    discountAllocation: Math.round(Number(discountAllocation) * 100),
    fundingSource,
  };
};

export const getScanDataConfigurationOperation = ({
  offerId,
  values,
  dirtyFields,
}: GetScanDataConfigurationOperationParams): Operation | null => {
  if (
    !isFieldDirty<{ scanDataConfiguration?: ScanDataConfigurationFormValues }>(
      dirtyFields,
      'scanDataConfiguration',
    )
  ) {
    return null;
  }

  if (values.scanDataConfiguration?.isEnabled) {
    return makeOfferUpdateOperation(offerId, {
      scanDataConfiguration: buildScanDataConfigurationInput(values.scanDataConfiguration),
    });
  }

  return makeOfferUpdateOperation(offerId, {
    scanDataConfiguration: null,
  });
};
